import { useEffect, useState } from 'react';

const MOBILE_QUERY = '(max-width: 768px)';

const isMobileViewport = () =>
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(MOBILE_QUERY).matches;

const readStoredTab = (storageKey) => {
    try {
        return window.sessionStorage.getItem(storageKey);
    } catch {
        return null;
    }
};

/**
 * Pestaña activa que sobrevive a una recarga de la página en el celular: el
 * sistema descarta la pestaña del navegador (o la app instalada) cuando se
 * cambia de aplicación y al volver la planilla arrancaba siempre en la primera
 * pestaña. En escritorio se mantiene el comportamiento de siempre.
 */
export const usePersistentTab = (storageKey, defaultTab, validTabs) => {
    const [activeTab, setActiveTab] = useState(() => {
        if (!isMobileViewport()) return defaultTab;
        const stored = readStoredTab(storageKey);
        return stored && validTabs.includes(stored) ? stored : defaultTab;
    });

    useEffect(() => {
        try {
            window.sessionStorage.setItem(storageKey, activeTab);
        } catch {
            // Sin storage (modo privado): solo se pierde el recuerdo de la pestaña.
        }
    }, [storageKey, activeTab]);

    return [activeTab, setActiveTab];
};

/**
 * Centra la pestaña activa en una barra con scroll horizontal, para que en el
 * celular nunca quede cortada contra el borde. Devuelve el ref para la barra;
 * la pestaña activa se marca con aria-selected="true".
 */
export const useCenterActiveTab = (activeTab) => {
    const [container, setContainer] = useState(null);

    useEffect(() => {
        if (!container || container.scrollWidth <= container.clientWidth) return;
        const active = container.querySelector('[aria-selected="true"]');
        if (!active) return;
        const containerRect = container.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        const delta = (activeRect.left + activeRect.width / 2) - (containerRect.left + containerRect.width / 2);
        if (typeof container.scrollBy === 'function') {
            container.scrollBy({ left: delta, behavior: 'smooth' });
        }
    }, [container, activeTab]);

    return setContainer;
};
